import { z } from 'zod';

/**
 * Classic draw dominoes with a double-six set: 2–4 players get 7 tiles each, the rest is the boneyard. A player who
 * cannot play draws until a tile fits (or the boneyard is empty) and otherwise passes. Emptying your hand wins the
 * round and scores the pips left in the other hands; a blocked round goes to the lowest hand. First to the target
 * (101 by default) wins the game and the pot.
 */

/** A tile as two pip counts. In a hand `a <= b`; on the table it is oriented left → right. */
export type DominoTile = readonly [number, number];
export type DominoEnd = 'L' | 'R';

export const DOMINO_MAX_PIP = 6;
export const DOMINO_HAND_SIZE = 7;
export const DOMINO_SEAT_OPTIONS = [2, 3, 4] as const;
export const DOMINO_STAKE_OPTIONS = [0, 10, 50, 100] as const;

/** "a-b" with a <= b: how tiles travel between server and app. */
export function tileKey(t: DominoTile): string {
  return t[0] <= t[1] ? `${t[0]}-${t[1]}` : `${t[1]}-${t[0]}`;
}

export function parseTileKey(key: string): DominoTile {
  const m = /^([0-6])-([0-6])$/.exec(key);
  if (!m) throw new Error(`bad tile ${key}`);
  const a = Number(m[1]);
  const b = Number(m[2]);
  return a <= b ? [a, b] : [b, a];
}

export function pips(t: DominoTile): number {
  return t[0] + t[1];
}

export function isDouble(t: DominoTile): boolean {
  return t[0] === t[1];
}

/** The 28 tiles of a double-six set. */
export function fullDominoSet(): DominoTile[] {
  const tiles: DominoTile[] = [];
  for (let a = 0; a <= DOMINO_MAX_PIP; a++) for (let b = a; b <= DOMINO_MAX_PIP; b++) tiles.push([a, b]);
  return tiles;
}

/** Open ends of the line of play, or null before the first tile. */
export interface DominoEnds {
  left: number;
  right: number;
}

/** Where `tile` can go: both ends, one, or none. Any tile fits an empty table (reported as 'L'). */
export function fitsEnds(tile: DominoTile, ends: DominoEnds | null): DominoEnd[] {
  if (!ends) return ['L'];
  const out: DominoEnd[] = [];
  if (tile[0] === ends.left || tile[1] === ends.left) out.push('L');
  if (tile[0] === ends.right || tile[1] === ends.right) out.push('R');
  return out;
}

export type DominoRoomStatus = 'WAITING' | 'PLAYING' | 'FINISHED' | 'CANCELLED';

export interface DominoPlayerDto {
  userId: string;
  seat: number;
  name: string;
  photoUrl: string | null;
  vip: boolean;
  frame: string | null;
  score: number;
  /** Tiles in hand (the tiles themselves are only shown to their owner, or to all after a round). */
  tiles: number;
  /** Left the game or timed out too often: out of the game, stake lost. */
  out: boolean;
}

export interface DominoRoundResultDto {
  round: number;
  /** null: a blocked round with a tie for the lowest hand (nobody scores). */
  winnerSeat: number | null;
  points: number;
  blocked: boolean;
  /** Every hand at the end of the round. */
  hands: { seat: number; tiles: DominoTile[]; pips: number }[];
}

export interface DominoLastMoveDto {
  seat: number;
  kind: 'play' | 'draw' | 'pass' | 'auto';
  tile?: DominoTile;
  end?: DominoEnd;
  drew?: number;
}

/** A room as one player sees it. */
export interface DominoViewDto {
  id: string;
  status: DominoRoomStatus;
  seats: number;
  stake: number;
  /** Sum of the stakes; the winner gets it minus the house share. */
  pot: number;
  target: number;
  round: number;
  players: DominoPlayerDto[];
  /** The line of play, left to right, each tile oriented. */
  chain: DominoTile[];
  ends: DominoEnds | null;
  boneyard: number;
  turnSeat: number | null;
  /** Identifies the current turn; actions name it so a repeated tap acts once. */
  turnId: string | null;
  /** Epoch ms when the current turn (or the pause after a round) ends. */
  deadline: number | null;
  /** Length of a turn, for the timer bar. */
  turnMs: number;
  serverNow: number;
  mySeat: number | null;
  hand: DominoTile[];
  /** On my turn: which tiles fit which end; empty means draw or pass. */
  playable: { tile: string; ends: DominoEnd[] }[];
  canDraw: boolean;
  canPass: boolean;
  lastMove: DominoLastMoveDto | null;
  lastRound: DominoRoundResultDto | null;
  winnerUserId: string | null;
  payout: number | null;
}

export interface DominoRoomListItemDto {
  id: string;
  seats: number;
  stake: number;
  createdAt: string;
  players: { userId: string; name: string; photoUrl: string | null; vip: boolean }[];
}

export interface DominoLobbyDto {
  enabled: boolean;
  stakesEnabled: boolean;
  stakes: number[];
  target: number;
  rakePercent: number;
  /** The room this player is in (waiting or playing), to go back to it. */
  current: string | null;
  rooms: DominoRoomListItemDto[];
}

export const dominoCreateSchema = z
  .object({
    seats: z.union([z.literal(2), z.literal(3), z.literal(4)]),
    stake: z
      .number()
      .int()
      .refine((v) => (DOMINO_STAKE_OPTIONS as readonly number[]).includes(v), 'unknown stake'),
  })
  .strict();

export const dominoQuickSchema = z
  .object({
    stake: z
      .number()
      .int()
      .refine((v) => (DOMINO_STAKE_OPTIONS as readonly number[]).includes(v), 'unknown stake'),
    seats: z.union([z.literal(2), z.literal(3), z.literal(4)]).optional(),
  })
  .strict();

export const dominoMoveSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('play'),
      turnId: z.string().max(32),
      tile: z.string().regex(/^[0-6]-[0-6]$/),
      end: z.enum(['L', 'R']),
    })
    .strict(),
  z.object({ kind: z.literal('draw'), turnId: z.string().max(32) }).strict(),
  z.object({ kind: z.literal('pass'), turnId: z.string().max(32) }).strict(),
]);
export type DominoMove = z.infer<typeof dominoMoveSchema>;
